import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useEffect, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";
import { themeSettings, newMuiTheme } from "./styles/Theme";
import SiteRoutes from "./routes/Index";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/style.css";
import "react-querybuilder/dist/query-builder.scss";
import "./styles/styles.scss";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AuthProvider } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { NavigationHistoryProvider } from "./contexts/NavigationHistoryContext";
import { PaginationProvider } from "./contexts/PaginationContext";
import WebSocketProvider from "./components/WebSocketProvider";
import { ThemeModeProvider, useThemeMode } from "./contexts/ThemeModeContext";
import { ThemeVersionProvider } from "./contexts/ThemeContext";
import { getConfig } from "./config/runtimeConfig";

/**
 * AppShell — rendered inside ThemeModeProvider so it can call useThemeMode()
 * for light/dark mode (DQ-8621). ThemeVersionProvider is nested inside
 * AuthProvider so isAdmin is available to any consumer, and outside
 * ThemeProvider so it can drive the legacy/new theme toggle (DQ-8526).
 */
const AppShell: React.FC = () => {
  // Theme mode from context: header toggle, localStorage persistence,
  // and prefers-color-scheme default (DQ-8621)
  const { mode } = useThemeMode();
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  // Clear notifications from localStorage on page refresh/reload
  useEffect(() => {
    localStorage.removeItem("notifications");
  }, []);

  return (
    <div className="app">
      <AuthProvider>
        {/* ThemeVersionProvider sits inside AuthProvider so the full auth  */}
        {/* tree (including isAdmin) is available to any consumer, and      */}
        {/* outside ThemeProvider so it can drive theme selection (DQ-8526).*/}
        <ThemeVersionProvider>
          <PaginationProvider>
            <NotificationProvider>
              <WebSocketProvider />
              <BrowserRouter>
                <NavigationHistoryProvider>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <ToastContainer
                      theme={mode}
                      autoClose={
                        Number(getConfig("REACT_APP_TOAST_TIMEOUT_DURATION")) ||
                        5000
                      }
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <SiteRoutes />
                    </LocalizationProvider>
                  </ThemeProvider>
                </NavigationHistoryProvider>
              </BrowserRouter>
            </NotificationProvider>
          </PaginationProvider>
        </ThemeVersionProvider>
      </AuthProvider>
    </div>
  );
};

const App: React.FC = () => (
  <ThemeModeProvider>
    <AppShell />
  </ThemeModeProvider>
);

export default App;
